import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatDate } from "@/utils/formatDate";
import {
  useAddStage,
  useConstructionBoard,
  useDeleteStage,
  usePatchStage,
  useSeedStages,
} from "../hooks";
import { STAGE_STATE_LABELS } from "../types";
import type { ConstructionStageRow, ConstructionStageState } from "../types";

const STATES: ConstructionStageState[] = ["not_started", "in_progress", "done"];

function StageRow({
  stage,
  projectId,
  canEdit,
}: {
  stage: ConstructionStageRow;
  projectId: string;
  canEdit: boolean;
}) {
  const patch = usePatchStage(projectId);
  const remove = useDeleteStage(projectId);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(stage.note);

  const tone = stage.is_ready
    ? "border-l-emerald-500"
    : stage.state === "done"
      ? "border-l-gray-300"
      : "border-l-amber-400";

  return (
    <div
      className={`border-l-4 ${tone} border-t border-gray-100 px-3 py-2 dark:border-gray-700`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-sm ${stage.state === "done" ? "text-gray-400 line-through" : "text-gray-900 dark:text-gray-100"}`}
            >
              {stage.label}
            </span>
            {stage.is_ready && (
              <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] uppercase text-emerald-700">
                nasz ruch
              </span>
            )}
            {stage.out_of_order && (
              <span
                className="rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] uppercase text-amber-700"
                title="Zrobiony mimo niezrobionych wymagań"
              >
                poza kolejnością
              </span>
            )}
            {stage.is_custom && (
              <span className="font-mono text-[10px] text-gray-400">własny</span>
            )}
          </div>
          {stage.blocked_by.length > 0 && stage.state !== "done" && (
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              czeka na: {stage.blocked_by.map((b) => b.label).join(", ")}
            </p>
          )}
          {stage.note && !noteOpen && (
            <p className="mt-0.5 whitespace-pre-wrap text-[11px] text-gray-500 dark:text-gray-400">
              {stage.note}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {stage.completed_at && (
            <span className="font-mono text-[11px] text-gray-400">
              {formatDate(stage.completed_at)}
            </span>
          )}
          <DatePicker
            className="w-40 px-2 py-0.5 font-mono text-[12px]"
            value={stage.due_date}
            disabled={!canEdit || patch.isPending}
            placeholder="Termin"
            aria-label="Termin"
            onChange={(v) =>
              patch.mutate({
                id: stage.id,
                data: { due_date: v || null },
              })
            }
          />
          <select
            className="input py-0.5 text-[12px]"
            value={stage.state}
            disabled={!canEdit || patch.isPending}
            onChange={(e) =>
              patch.mutate({
                id: stage.id,
                data: { state: e.target.value as ConstructionStageState },
              })
            }
          >
            {STATES.map((s) => (
              <option key={s} value={s}>
                {STAGE_STATE_LABELS[s]}
              </option>
            ))}
          </select>
          {canEdit && (
            <button
              className="text-[11px] text-gray-400 hover:text-[var(--color-primary)]"
              onClick={() => setNoteOpen((v) => !v)}
            >
              Notatka
            </button>
          )}
          {canEdit && stage.is_custom && (
            <button
              className="text-[11px] text-gray-400 hover:text-red-500"
              onClick={() => remove.mutate(stage.id)}
            >
              Usuń
            </button>
          )}
        </div>
      </div>

      {noteOpen && (
        <div className="mt-1.5 flex items-start gap-2">
          <textarea
            rows={2}
            className="input flex-1 text-[12px]"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            size="sm"
            loading={patch.isPending}
            onClick={() =>
              patch.mutate(
                { id: stage.id, data: { note } },
                { onSuccess: () => setNoteOpen(false) },
              )
            }
          >
            Zapisz
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Plansza etapów budowy.
 *
 * Świadomie lekka: trzy stany, termin, notatka i graf zależności — bez faz, SLA
 * i propozycji AI, które niosą kamienie projektowe. Na budowie brygada albo weszła,
 * albo nie weszła; cała wartość planszy siedzi w jednym pytaniu „co mogę teraz robić",
 * na które odpowiada znacznik „nasz ruch".
 */
export function ConstructionBoard({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const { data, isLoading } = useConstructionBoard(projectId);
  const seed = useSeedStages(projectId);
  const add = useAddStage(projectId);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [dependsOn, setDependsOn] = useState<string[]>([]);

  if (isLoading) return <p className="section-label">Ładowanie…</p>;
  if (!data) return null;

  const { stages, summary } = data;

  if (stages.length === 0) {
    return (
      <div className="card p-4">
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
          Ta budowa nie ma jeszcze etapów.
        </p>
        {canEdit && (
          <Button size="sm" loading={seed.isPending} onClick={() => seed.mutate()}>
            Zasiej etapy z szablonu
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-[12px] text-gray-500 dark:text-gray-400">
        <span>
          Zrobione: <strong className="text-gray-900 dark:text-gray-100">{summary.done}</strong>/
          {summary.total}
        </span>
        <span>
          W toku: <strong>{summary.in_progress}</strong>
        </span>
        <span className="text-emerald-700">
          Nasz ruch: <strong>{summary.ready}</strong>
        </span>
        {summary.out_of_order > 0 && (
          <span className="text-amber-700">
            Poza kolejnością: <strong>{summary.out_of_order}</strong>
          </span>
        )}
      </div>

      <div className="card overflow-hidden">
        {stages.map((s) => (
          <StageRow key={s.id} stage={s} projectId={projectId} canEdit={canEdit} />
        ))}
      </div>

      {canEdit &&
        (adding ? (
          <div className="card space-y-2 p-3">
            <input
              className="input w-full text-[13px]"
              value={label}
              placeholder="np. Przewiert pod torami"
              onChange={(e) => setLabel(e.target.value)}
            />
            <div>
              <p className="mb-1 text-[11px] text-gray-500">Czeka na (opcjonalnie):</p>
              <div className="flex flex-wrap gap-1.5">
                {stages.map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-1 text-[11px]"
                  >
                    <input
                      type="checkbox"
                      className="accent-[var(--color-primary)]"
                      checked={dependsOn.includes(s.id)}
                      onChange={(e) =>
                        setDependsOn((cur) =>
                          e.target.checked
                            ? [...cur, s.id]
                            : cur.filter((x) => x !== s.id),
                        )
                      }
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={!label.trim()}
                loading={add.isPending}
                onClick={() =>
                  add.mutate(
                    { label: label.trim(), depends_on: dependsOn },
                    {
                      onSuccess: () => {
                        setAdding(false);
                        setLabel("");
                        setDependsOn([]);
                      },
                    },
                  )
                }
              >
                Dodaj etap
              </Button>
              <button
                className="text-[12px] text-gray-400 hover:underline"
                onClick={() => setAdding(false)}
              >
                Anuluj
              </button>
            </div>
          </div>
        ) : (
          <button
            className="text-[12px] text-[var(--color-primary)] hover:underline"
            onClick={() => setAdding(true)}
          >
            + Własny etap
          </button>
        ))}
    </div>
  );
}
